import { useEffect, useRef } from "react";

/**
 * Re-seeds `form`/`initial` from freshly fetched detail data, but only while
 * the form is pristine — background refetches (focus refetch, invalidation
 * from another tab) must never clobber an in-progress edit. `isDirty` is
 * read via a ref so the effect doesn't re-run on every keystroke; it only
 * re-fires when `detailData` itself changes.
 *
 * Does not handle post-save pristine sync — after a successful mutation,
 * call `setInitial(savedForm)` directly from the mutation's `onSuccess`
 * (using the mutation's `variables`, not a closed-over `form`, to avoid
 * stale-closure bugs).
 */
export function useDirtyGuardedSeed<TDetail, TForm>(
  detailData: TDetail | undefined,
  isDirty: boolean,
  toForm: (detail: TDetail) => TForm,
  setForm: (form: TForm) => void,
  setInitial: (form: TForm) => void,
) {
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (detailData && !isDirtyRef.current) {
      const f = toForm(detailData);
      setForm(f);
      setInitial(f);
    }
    // toForm/setForm/setInitial are expected to be referentially stable
    // (module-level function + useState setters) — only detailData actually
    // varies, matching the pre-extraction effect's `[detail.data]` deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData]);
}
